import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import { THEME_PRESETS } from '../../theme';
import { FW, LS, WHW } from '../../theme/tokens';
import { useLogoPreview } from '../../branding/BrandLogo';
import { LOGO_VARIANTS } from '../../branding/logos';

const SWATCHES = {
  fabworks: [FW.navy, FW.steel, FW.surface, FW.accent, FW.success],
  langley: [LS.black, LS.gray, LS.surface, LS.line, LS.success],
  whw: [WHW.black, WHW.red, WHW.surface, WHW.line, WHW.success],
  classic: ['#0d47a1', '#1976d2', '#ffffff', '#dc004e', '#2e7d32'],
};

const AppearanceManager = () => {
  const { preset, serverPreset, localOverride, setLocalOverride, saveServerPreset } = useThemeMode();
  const { logoId, setLogoPreview, clearLogoPreview, isPreview } = useLogoPreview();
  const [selection, setSelection] = useState(serverPreset);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setSelection(serverPreset);
  }, [serverPreset]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await saveServerPreset(selection);
      setMessage('Company theme updated. Everyone on this site picks it up on their next page load.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sets the look and feel for this company. Printed documents (quotes, orders, packing
        slips, invoices) are not affected.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {THEME_PRESETS.map((option) => {
          const selected = selection === option.id;
          return (
            <Paper
              key={option.id}
              variant="outlined"
              onClick={() => setSelection(option.id)}
              sx={{
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                borderColor: selected ? 'primary.main' : 'divider',
                borderWidth: selected ? 2 : 1,
              }}
            >
              <Radio checked={selected} size="small" />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontWeight: 700 }}>{option.label}</Typography>
                  {serverPreset === option.id && (
                    <Chip size="small" label="Current" color="primary" variant="outlined" />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                {(SWATCHES[option.id] || []).map((color) => (
                  <Box
                    key={color}
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '4px',
                      bgcolor: color,
                      border: '1px solid rgba(0,0,0,0.12)',
                    }}
                  />
                ))}
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || selection === serverPreset}
        >
          {saving ? 'Saving…' : 'Apply to everyone'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Company setting: <strong>{serverPreset}</strong>
        </Typography>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Preview on this device only
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Try a theme in your own browser without changing it for anyone else. Currently showing{' '}
          <strong>{preset}</strong>
          {localOverride ? ' (personal preview)' : ' (company setting)'}.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {THEME_PRESETS.map((option) => (
            <Button
              key={option.id}
              size="small"
              variant={localOverride === option.id ? 'contained' : 'outlined'}
              onClick={() => setLocalOverride(option.id)}
            >
              Preview {option.label}
            </Button>
          ))}
          <Button size="small" onClick={() => setLocalOverride(null)} disabled={!localOverride}>
            Use company setting
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Logo preview (this device only)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Swap the Fab Works mark in the main app and Sales Portal sidebars. Header and printed document
          logos stay on the production logo. Active:{' '}
          <strong>{LOGO_VARIANTS.find((v) => v.id === logoId)?.label || logoId}</strong>
          {isPreview ? ' (personal preview)' : ' (production)'}.
        </Typography>

        <Stack spacing={1.25}>
          {LOGO_VARIANTS.map((option) => {
            const selected = logoId === option.id;
            return (
              <Paper
                key={option.id}
                variant="outlined"
                onClick={() => setLogoPreview(option.id)}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  cursor: 'pointer',
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderWidth: selected ? 2 : 1,
                }}
              >
                <Radio checked={selected} size="small" />
                <Box
                  sx={{
                    width: 120,
                    height: 72,
                    flexShrink: 0,
                    borderRadius: 1,
                    bgcolor: FW.navy,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1,
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: option.plateOnDark ? 'rgba(255,255,255,0.96)' : 'transparent',
                      borderRadius: 1,
                      px: 0.75,
                      py: 0.5,
                      maxWidth: '100%',
                      maxHeight: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      component="img"
                      src={option.preview}
                      alt={option.label}
                      sx={{ maxWidth: '100%', maxHeight: 56, objectFit: 'contain', display: 'block' }}
                    />
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: 120,
                    height: 72,
                    flexShrink: 0,
                    borderRadius: 1,
                    bgcolor: '#F3F6F9',
                    border: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={option.preview}
                    alt=""
                    sx={{ maxWidth: '100%', maxHeight: 56, objectFit: 'contain', display: 'block' }}
                  />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>{option.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Stack>

        <Button
          size="small"
          sx={{ mt: 1.5 }}
          onClick={clearLogoPreview}
          disabled={!isPreview}
        >
          Use theme default logo
        </Button>
      </Paper>
    </Box>
  );
};

export default AppearanceManager;
